import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Collapse, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CodeText, ScopeLevelChip } from './OntologyBrowser';
import {
    OntologyKind, KIND_LABEL, conceptHref, displayName, parameterHref, policyHref,
} from './ontologyFormat';
import { agentHref, getOntologyCoverage, normalizeCoverage, ontologyErrorMessage } from '../../api/ontologyApi';
import { dsVar, ink } from '../../theme/tokens';

/**
 * Where the live configuration has gaps.
 *
 * The control plane validates these same conditions when a document is WRITTEN — a
 * threshold bound to a concept that does not resolve, a policy naming a retired term — but
 * nothing re-checks a document after the thing it points at is retired. A library that was
 * consistent when every piece was saved drifts silently, and until now nothing in the
 * product would say so.
 *
 * Reported in both directions. The library-side findings name a concept, policy or
 * threshold with something wrong on it; the agent-side ones name an AGENT that has no link
 * back to any of them — running on ad-hoc settings, with no traceable basis. The second is
 * the harder finding, and until now nothing rolled it up.
 *
 * Deliberately NOT built on OntologyBrowser: this is not a paged server-side list of one
 * collection, it is a handful of findings over all three. It borrows the chevron +
 * Collapse idiom from PolicyParametersSection and UsedByTemplatesSection instead.
 */

/**
 * `'activities'` is deliberately not folded into OntologyKind. OntologyPage keeps non-kinds
 * out of KINDS so they cannot leak into apiForKind, codeOf or the preview drawer, and an
 * activity template has none of those: no ontology API path, no detail route, no scope
 * level. It is widened here, at the one place that renders it.
 */
type CoverageItemKind = OntologyKind | 'activities';

interface CoverageItem {
    kind: CoverageItemKind;
    code: string;
    label: string;
    /** Absent on an activity template, which has no ontology scope level. */
    scope_level?: string;
    /** What is wrong, when the code alone does not say — the dangling reference. */
    detail?: string;
    /** Activity templates only: how many clone variants the family collapsed to. */
    variant_count?: number;
}

interface CoverageFinding {
    id: string;
    /**
     * Which side of the report this is — 'library' or 'agents'. Absent on a control plane
     * that predates the agent-side findings, which is why every read of it falls back
     * rather than branching: that server's six findings then form one group and the page
     * renders as the single column it used to be.
     */
    group?: string;
    severity: 'warning' | 'info';
    title: string;
    explanation: string;
    count: number;
    items: CoverageItem[];
    truncated?: boolean;
}

/** The shape normalizeCoverage produces; declared here because the api module is JS. */
interface CoverageReport {
    generatedAt: string | null;
    totals: {
        concepts: number;
        policies: number;
        parameters: number;
        /** Clone FAMILIES, not stored documents — one count per agent. */
        activity_templates: number;
    };
    findings: CoverageFinding[];
}

const hrefFor = (item: CoverageItem): string | null => {
    if (item.kind === 'concepts') return conceptHref(item.code);
    if (item.kind === 'policies') return policyHref(item.code);
    // An agent has no ontology detail route; its home is the marketplace drawer. Branched
    // BEFORE the parameter fallback, which would otherwise link a template code to a
    // threshold page that does not exist.
    if (item.kind === 'activities') return agentHref(item.code);
    return parameterHref(item.code);
};

/** KIND_LABEL is typed to the three kinds, and this list carries a fourth. */
const ITEM_LABEL: Record<CoverageItemKind, string> = { ...KIND_LABEL, activities: 'Agent' };

const DEFAULT_GROUP = 'library';

/**
 * What each column is called. The only server vocabulary this file carries, and it is two
 * words rather than the ten finding ids the alternative would have needed — a column
 * heading has to say something, and only the client knows it is a column.
 *
 * A group with no entry here falls back to its own id, so a third group added server-side
 * appears as a column rather than disappearing.
 */
const GROUP_TITLE: Record<string, string> = {
    library: 'Library gaps',
    agents: 'Agent gaps',
    vocabulary: 'Vocabulary gaps',
};

const GROUP_BLURB: Record<string, string> = {
    library: 'Terms, policies and thresholds that point at something missing — or that '
        + 'nothing reaches.',
    agents: 'Agents running with no link back to the library.',
    vocabulary: 'Your monitoring rules that raise alerts but are not mapped to a typology.',
};

/**
 * Split the findings into columns, keeping the server's order both within a group and
 * between groups.
 *
 * Grouped rather than sliced at a fixed index: the report decides how many findings each
 * side has, and a client that assumed "the first six" would put a seventh library finding
 * under the agents heading the day one is added.
 */
const byGroup = (findings: CoverageFinding[]) => {
    const groups: { id: string; findings: CoverageFinding[] }[] = [];
    for (const finding of findings) {
        const id = finding.group || DEFAULT_GROUP;
        const existing = groups.find((group) => group.id === id);
        if (existing) existing.findings.push(finding);
        else groups.push({ id, findings: [finding] });
    }
    return groups;
};

const severityColor = (severity: string) => (severity === 'info' ? '#7DD3FC' : '#FBBF24');

/**
 * One finding, collapsed by default.
 *
 * `focused` is how an arriving link opens the right card: a banner elsewhere summarises a
 * finding in one line and sends the reader here for the full list, and landing on a stack
 * of ten closed cards would make them hunt for the one they clicked. The card opens itself
 * and scrolls into view instead.
 */
const FindingCard: React.FC<{ finding: CoverageFinding; focused?: boolean }> = ({
    finding, focused = false,
}) => {
    const clean = finding.count === 0;
    // A satisfied check has nothing to open, so a link to one lands on the card without
    // expanding an empty list.
    const [open, setOpen] = useState(focused && !clean);
    const cardRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!focused) return;
        if (!clean) setOpen(true);
        // Guarded: jsdom leaves scrollIntoView undefined, and a card that cannot scroll
        // itself into view is still a correctly opened card.
        cardRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    }, [focused, clean]);

    const colour = clean ? '#4ADE80' : severityColor(finding.severity);

    return (
        <Box
            ref={cardRef}
            data-finding={finding.id}
            sx={{
                border: `1px solid ${focused ? '#B4884A' : dsVar('line')}`,
                borderRadius: '8px',
                backgroundColor: dsVar('surface'),
                mb: 1.5,
                overflow: 'hidden',
            }}
        >
            <Box
                component={clean ? 'div' : 'button'}
                type={clean ? undefined : 'button'}
                onClick={clean ? undefined : () => setOpen((value) => !value)}
                aria-expanded={clean ? undefined : open}
                sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%',
                    px: 2, py: 1.75, border: 'none', background: 'transparent',
                    textAlign: 'left', fontFamily: '"Inter", sans-serif',
                    cursor: clean ? 'default' : 'pointer',
                    '&:hover': { backgroundColor: clean ? 'transparent' : dsVar('raised') },
                }}
            >
                <Box sx={{ color: colour, display: 'flex', pt: '2px' }}>
                    {clean
                        ? <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                        : finding.severity === 'info'
                            ? <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                            : <WarningAmberIcon sx={{ fontSize: 18 }} />}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                            component="span"
                            sx={{ fontSize: '0.875rem', fontWeight: 600, color: dsVar('fg-body') }}
                        >
                            {finding.title}
                        </Typography>
                        <Chip
                            size="small"
                            label={clean ? 'None' : finding.count}
                            sx={{
                                height: 20, fontSize: '0.6875rem', fontWeight: 600,
                                color: colour, backgroundColor: 'transparent',
                                border: `1px solid ${colour}`,
                            }}
                        />
                    </Box>
                    <Typography
                        sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), mt: 0.5, lineHeight: 1.6 }}
                    >
                        {finding.explanation}
                    </Typography>
                </Box>

                {!clean && (
                    <ChevronRightIcon
                        sx={{
                            fontSize: 20, color: dsVar('fg-muted'), flexShrink: 0,
                            transform: open ? 'rotate(90deg)' : 'none',
                            transition: 'transform 150ms',
                        }}
                    />
                )}
            </Box>

            <Collapse in={open} unmountOnExit>
                <Box sx={{ borderTop: `1px solid ${dsVar('line')}` }}>
                    {finding.items.map((item) => (
                        <Box
                            key={`${item.kind}-${item.code}`}
                            component="a"
                            href={hrefFor(item) ?? undefined}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5,
                                px: 2, py: 1.25, textDecoration: 'none',
                                borderBottom: `1px solid ${dsVar('line')}`,
                                '&:last-of-type': { borderBottom: 'none' },
                                '&:hover': { backgroundColor: dsVar('raised') },
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft') }}>
                                    {displayName(item.label, item.code)}
                                </Typography>
                                <CodeText>{item.code}</CodeText>
                            </Box>

                            {item.detail && (
                                <Typography
                                    sx={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontSize: '0.6875rem', color: ink('#F87171'),
                                    }}
                                >
                                    → {item.detail}
                                </Typography>
                            )}

                            <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                                {ITEM_LABEL[item.kind]}
                            </Typography>

                            {/* An agent has no scope level, and ScopeLevelChip renders a
                                bare em-dash for one — a chip that says nothing. The
                                version count goes in that slot instead, and only where
                                the family actually has clones. */}
                            {item.scope_level
                                ? <ScopeLevelChip scopeLevel={item.scope_level} />
                                : (item.variant_count ?? 0) > 1 && (
                                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                                        {item.variant_count} versions
                                    </Typography>
                                )}
                        </Box>
                    ))}

                    {finding.truncated && (
                        <Typography
                            sx={{ px: 2, py: 1.25, fontSize: '0.75rem', color: dsVar('fg-muted') }}
                        >
                            Showing the first {finding.items.length} of {finding.count}.
                        </Typography>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};

interface CoverageBrowserProps {
    /**
     * The finding id to open on arrival, from `?finding=` on the tab's URL.
     *
     * Passed down rather than read here with useSearchParams: this component is rendered by
     * OntologyPage, which already holds the query string, and taking the hook would make
     * the report untestable outside a Router for a single string.
     */
    focusFinding?: string | null;
}

const CoverageBrowser: React.FC<CoverageBrowserProps> = ({ focusFinding = null }) => {
    const [report, setReport] = useState<CoverageReport | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        let active = true;

        (async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await getOntologyCoverage({ signal: controller.signal });
                if (active) setReport(normalizeCoverage(res) as CoverageReport);
            } catch (err: any) {
                if (!active || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                setError(ontologyErrorMessage(err, 'Could not build the coverage report.'));
            } finally {
                if (active) setLoading(false);
            }
        })();

        return () => { active = false; controller.abort(); };
    }, []);

    const outstanding = (report?.findings ?? []).reduce((sum, f) => sum + f.count, 0);

    // Written once because both branches of the summary say it. Agents are counted in clone
    // families, so this is "how many agents", not how many stored templates.
    const groups = byGroup(report?.findings ?? []);

    const totals = report?.totals;
    const scanned = totals
        ? `${totals.concepts} terms, ${totals.policies} policies, `
          + `${totals.parameters} thresholds and ${totals.activity_templates} agents`
        : '';

    return (
        <Box>
            <Box sx={{ mb: 2.5 }}>
                <Typography
                    component="h2"
                    sx={{ fontSize: '1rem', fontWeight: 600, color: dsVar('fg-body') }}
                >
                    Coverage
                </Typography>
                <Typography
                    sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), mt: 0.25, maxWidth: '48rem', lineHeight: 1.6 }}
                >
                    Where the configuration that is live right now has gaps, read from both
                    ends. Drafts are not counted; a gap inside unapproved work is not yet a
                    finding.
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                    <CircularProgress size={24} sx={{ color: dsVar('accent-fg') }} />
                </Box>
            )}

            {!loading && report && (
                <>
                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted'), mb: 2 }}>
                        {outstanding === 0
                            ? `Nothing outstanding across ${scanned}.`
                            : `${outstanding} item${outstanding === 1 ? '' : 's'} to review `
                              + `across ${scanned}.`}
                    </Typography>

                    {/* One column per side of the report, rather than ten cards in a
                        single stack whose halfway point is invisible. Two independent
                        grid columns, NOT CSS multi-column: a card expanding its Collapse
                        inside a `columnCount` container reflows every card after it and
                        can split a card box across the column break. Here an expansion
                        only moves what is beneath it in its own column.

                        Capped at two columns because that is what the width holds; a
                        third group would wrap onto a second row rather than squeeze.
                        Stacked below `lg`, where two columns of this text are cramped —
                        and with one group the template is a single column anyway, which
                        is how an older control plane's response renders. */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                lg: `repeat(${Math.min(groups.length, 2)}, minmax(0, 1fr))`,
                            },
                            gap: 3,
                            alignItems: 'start',
                        }}
                    >
                        {groups.map((group) => {
                            const open = group.findings.reduce((sum, f) => sum + f.count, 0);
                            return (
                                <Box key={group.id} data-group={group.id}>
                                    {/* A heading earns its place by telling one column
                                        from the other. With a single group there is
                                        nothing to tell it from, and the page summary
                                        above already says what the report covers. */}
                                    {groups.length > 1 && (
                                        <Box sx={{ mb: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                                <Typography
                                                    component="h3"
                                                    sx={{
                                                        fontSize: '0.8125rem', fontWeight: 600,
                                                        color: dsVar('fg-body'), letterSpacing: '0.01em',
                                                    }}
                                                >
                                                    {GROUP_TITLE[group.id] ?? group.id}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted') }}>
                                                    {open === 0 ? 'all clear' : `${open} to review`}
                                                </Typography>
                                            </Box>
                                            {GROUP_BLURB[group.id] && (
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.75rem', color: dsVar('fg-2'),
                                                        mt: 0.25, lineHeight: 1.6,
                                                    }}
                                                >
                                                    {GROUP_BLURB[group.id]}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                    {/* Findings with a count of zero stay on screen as
                                        satisfied checks. "No dangling references" is an
                                        answer someone came here for, and hiding it would
                                        leave the page unable to give it. */}
                                    {group.findings.map((finding) => (
                                        <FindingCard
                                            key={finding.id}
                                            finding={finding}
                                            focused={finding.id === focusFinding}
                                        />
                                    ))}
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default CoverageBrowser;
